import React from 'react';
import { ActionsBar } from './components/ActionsBar/ActionsBar';
import { LoadingScreen } from './components/LoadingScreen/LoadingScreen';
import { Question } from './components/Question/Question';
import { StartPage } from './components/StartPage/StartPage';
import { StartPageLoading } from './components/StartPage/StartPage.loading';
import { useSurvey } from './useSurvey';
import { ANSWER_RESET_TIMEOUT, ANSWER_SELECTION_TIMEOUT } from './Survey.meta';
import { Timeout } from '../hooks/useTimeout';
import { ErrorState } from '../components/ErrorState';
import { useIsMobile } from '../../_hooks/useIsMobile';
import { useDocumentTitle } from '../../_hooks/useDocumentTitle';
import { BeFi20Logo } from '../img/BeFi20Logo';
import { logoAnimationVariants } from '../BeFi20.meta';
import { Header } from './components/StartPage/StartPage.styles';
import { Wrapper } from './Survey.styles';
import { Progress } from '../../CommonComponents/OPL/Progress/Progress';
import { BEFI20_DISABLE_ERROR_REGEX } from '../api/useSurveyQuery';
import { Box } from '../../CommonComponents/OPL/Box/Box';

export const Survey = () => {
  useDocumentTitle('BeFi20 Survey');

  const {
    data,
    isLoading,
    error,
    refetch,
    actions: {
      setCurrentQuestion,
      handleOnAnswer,
      handleOnBack,
      moveForwardTimeoutCallback,
      moveBackwardsTimeoutCallback,
    },
  } = useSurvey();

  const isMobile = useIsMobile();

  if (isLoading) {
    return <StartPageLoading logo={<BeFi20Logo />} />;
  }

  if (error) {
    const canRetry = !BEFI20_DISABLE_ERROR_REGEX.test(error.message);

    return (
      <ErrorState
        isMobile={false}
        message={error.message}
        onAction={canRetry ? refetch : undefined}
      />
    );
  }

  if (!data) {
    return null;
  }

  const {
    currentQuestion,
    questions,
    answers,
    postAnswersMutationBag,
    isMovingForward,
    isMovingBackwards,
    clientName,
  } = data;

  if (currentQuestion.index === -1) {
    return (
      <StartPage
        logo={<BeFi20Logo />}
        title={`What Does Money Mean to You, ${clientName}?`}
        description="Discover a clearer picture of your beliefs and values about money, so you and your advisor can better develop long-term financial plans. A partner or family member can do the same, so you can see how your beliefs and values align with theirs."
        helperText="Completing this assessment should take about 3 minutes."
        ctaText="Start Discovering"
        onStartSurvey={() => setCurrentQuestion({ index: 0, direction: 'forward' })}
      />
    );
  }

  if (postAnswersMutationBag.isLoading || postAnswersMutationBag.isSuccess) {
    return <LoadingScreen text="Generating Your Financial Persona" />;
  }

  return (
    <Wrapper key={`${isMobile}`}>
      {isMovingForward && (
        <Timeout cb={moveForwardTimeoutCallback} delay={ANSWER_SELECTION_TIMEOUT} />
      )}

      {isMovingBackwards && (
        <Timeout cb={moveBackwardsTimeoutCallback} delay={ANSWER_RESET_TIMEOUT} />
      )}

      <Progress
        value={((currentQuestion.index + 1) / questions.length) * 100}
        isLarge={!isMobile}
        isFlat
      />

      <ActionsBar
        isMobile={isMobile}
        isFirstQuestion={currentQuestion.index === 0}
        handleOnBack={() => handleOnBack(currentQuestion.index)}
      />

      {!isMobile && (
        <Header variants={logoAnimationVariants} initial="enter" animate="visible">
          <BeFi20Logo style={{ margin: '0 auto' }} />
        </Header>
      )}

      {/* This hack is needed to occupy the absolute positioned Questions space in the flow */}
      {isMobile && <Box height={450} />}

      <Question
        questions={questions}
        currentQuestion={currentQuestion}
        answers={answers}
        onAnswer={handleOnAnswer}
      />
    </Wrapper>
  );
};
